import { Theme } from '@emotion/react';
import { Box, FormControlLabel, SxProps, TextField } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledCheckbox, StyledCurrencyInput } from '../styles/StyledInputs';
import { StyledDocumentLink } from '../styles/StyledLink';
import { StyledCallout, StyledTypography } from '../styles/styles';

export function hasAdvisingFor(advisingFor?: string | null): advisingFor is string {
  return typeof advisingFor === 'string' && advisingFor.trim().length > 0;
}

export const AdvisingCallout: React.FC<{
  advisingFor: string;
  variant: 'suggesting' | 'submitting' | 'viewing' | 'updating' | 'withdrawing' | 'suggest-update';
}> = ({ advisingFor, variant }) => {
  const messages: Record<typeof variant, string> = {
    suggesting: `You are suggesting an EOI for ${advisingFor}.`,
    submitting: `You are submitting this EOI on behalf of ${advisingFor}.`,
    viewing: `You are viewing this EOI on behalf of ${advisingFor}.`,
    updating: `You are updating this EOI on behalf of ${advisingFor}.`,
    withdrawing: `You are withdrawing this EOI on behalf of ${advisingFor}.`,
    'suggest-update': `You are suggesting an update for ${advisingFor}.`,
  };

  return (
    <StyledCallout variant="warning" size="small">
      {messages[variant]}
    </StyledCallout>
  );
};

interface EOIConfirmCheckboxProps {
  confirmed: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  isCustodian?: boolean;
  sx?: SxProps<Theme>;
}

export const EOIConfirmCheckbox: React.FC<EOIConfirmCheckboxProps> = ({
  confirmed,
  onChange,
  disabled = false,
  isCustodian = false,
  sx,
}) => {
  const isDisabled = disabled || isCustodian;

  return (
    <Box className="fides-text eoi-check" display="flex" alignItems="center" gap={1} sx={sx}>
      <FormControlLabel
        control={
          <StyledCheckbox
            id="eoiCheckbox"
            checked={confirmed}
            disabled={isDisabled}
            style={{
              opacity: isDisabled ? 1 : undefined,
              pointerEvents: isDisabled ? 'none' : undefined,
            }}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label="Please confirm that you have read the EOI"
        sx={{
          '& .MuiFormControlLabel-label.Mui-disabled': {
            color: Palette['tone1'].buttonText,
          },
        }}
      />
    </Box>
  );
};

export const ReferrerLegalCheckbox: React.FC<{
  confirmed: boolean;
  onChange: (checked: boolean) => void;
  isCustodian?: boolean;
  sx?: SxProps<Theme>;
}> = ({ confirmed, onChange, isCustodian = false, sx }) => (
  <Box className="fides-text eoi-check" display="flex" alignItems="center" gap={1} sx={sx}>
    <FormControlLabel
      control={
        <StyledCheckbox
          checked={confirmed}
          disabled={isCustodian}
          style={{
            opacity: isCustodian ? 1 : undefined,
            pointerEvents: isCustodian ? 'none' : undefined,
          }}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={
        <span>
          I confirm that I have read and understood the{' '}
          <StyledDocumentLink
            href="/api/asset/content/legal/PrivacyPolicy.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </StyledDocumentLink>{' '}
          and{' '}
          <StyledDocumentLink
            href="/api/asset/content/legal/TermsAndConditions.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </StyledDocumentLink>
          .
        </span>
      }
      sx={{
        '& .MuiFormControlLabel-label.Mui-disabled': {
          color: Palette['tone1'].buttonText,
        },
      }}
    />
  </Box>
);

interface EOIInvestmentInputProps {
  investmentInput: string | undefined;
  validationError: string | undefined;
  editable: boolean;
  isCustodian?: boolean;
  onChange: (value: string | undefined) => void;
}

export const EOIInvestmentInput: React.FC<EOIInvestmentInputProps> = ({
  investmentInput,
  validationError,
  editable,
  isCustodian = false,
  onChange,
}) => (
  <div id="indicative-investment">
    <StyledCurrencyInput
      prefix="$"
      value={investmentInput}
      className={validationError ? 'input-error' : ''}
      name="indicative-investment"
      placeholder="Indicative investment"
      allowDecimals={false}
      allowNegativeValue={false}
      onValueChange={(value) => onChange(value ? value.toString() : undefined)}
      disabled={!editable || isCustodian}
      noDisabledGrey
    />
    {validationError ? (
      <StyledTypography tone="tone1" sx={{ mt: 1, fontSize: '0.875rem' }} className="input-error">
        {validationError}
      </StyledTypography>
    ) : null}
  </div>
);

export const SuggestionCommentField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <Box sx={{ mt: 2 }}>
    <TextField
      fullWidth
      multiline
      rows={3}
      label="Comment (optional)"
      placeholder="Add any additional comments about this suggestion..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        '& .MuiOutlinedInput-root': {
          fontFamily: 'Inter, sans-serif',
        },
        '& .MuiInputLabel-root': {
          fontFamily: 'Inter, sans-serif',
        },
      }}
    />
  </Box>
);
